import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useTheme, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";

const carouselItems = [
  {
    id: 1,
    bg: require("../../assets/images/querycards1.jpeg"),
    query: "Soft pastel color dresses for women for a day event.",
  },
  {
    id: 2,
    bg: require("../../assets/images/querycards2.jpeg"),
    query:
      "Vacation look for Women, Summer Dresses, Holiday Outfit matching accessories",
  },
  {
    id: 3,
    bg: require("../../assets/images/querycards3.jpeg"),
    query: " Professional and office-appropriate dresses and outfits for women",
  },
  {
    id: 4,
    bg: require("../../assets/images/querycards4.jpeg"),
    query: "Women 3 piece dress under Rs.15000",
  },
  {
    id: 5,
    bg: require("../../assets/images/querycards5.jpeg"),
    query: "Lightweight lawn fabric dresses for women for summer",
  },
  {
    id: 6,
    bg: require("../../assets/images/querycards6.jpeg"),
    query: " Formal and party shoes for men to match blue suit pants",
  },
  {
    id: 7,
    bg: require("../../assets/images/querycards7.jpeg"),
    query: " Matching printed dupatta for red plain dresses",
  },
  {
    id: 8,
    bg: require("../../assets/images/querycards8.jpeg"),
    query: "Men’s formal ties to match black suits and light blue shirt",
  },
  {
    id: 9,
    bg: require("../../assets/images/querycards9.jpeg"),
    query: "Kaftan dresses for her",
  },
  {
    id: 10,
    bg: require("../../assets/images/querycards10.jpeg"),
    query: "Mother’s Day gifts ideas",
  },
  {
    id: 11,
    bg: require("../../assets/images/querycards11.jpeg"),
    query: "Heavy Formal dresses for wedding season",
  },
  {
    id: 12,
    bg: require("../../assets/images/querycards12.jpeg"),
    query:
      "Women’s abaya collection including casual, embroidered, and luxury abayas",
  },
];

const FADE_DURATION = 800;
const SKELETON_HOLD = 600;
const AUTO_INTERVAL = 7000;

const QueryCards = ({ smallScreen = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const slidesToShow = isMobile ? 1 : isTablet ? 2 : 3;
  const rows = isMobile ? 4 : 1;
  const visibleCount = slidesToShow * rows;
  const total = carouselItems.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [contentOpacity, setContentOpacity] = useState(1);
  const [skeletonOpacity, setSkeletonOpacity] = useState(0);
  const isAnimating = useRef(false);
  const timerRef = useRef(null);

  const getVisibleItems = (startIndex) => {
    const items = [];
    for (let r = 0; r < rows; r++) {
      const rowItems = [];
      for (let c = 0; c < slidesToShow; c++) {
        const idx = (startIndex + r * slidesToShow + c) % total;
        rowItems.push(carouselItems[idx]);
      }
      items.push(rowItems);
    }
    return items;
  };

  const runTransition = () => {
    if (isAnimating.current) return;
    isAnimating.current = true;

    setContentOpacity(0);
    setSkeletonOpacity(1);

    setTimeout(() => {
      setCurrentIndex((prev) => (prev + visibleCount) % total);
    }, FADE_DURATION);

    setTimeout(() => {
      setSkeletonOpacity(0);
    }, FADE_DURATION + SKELETON_HOLD);

    setTimeout(
      () => {
        setContentOpacity(1);
        isAnimating.current = false;
      },
      FADE_DURATION + SKELETON_HOLD + FADE_DURATION,
    );
  };

  useEffect(() => {
    timerRef.current = setInterval(runTransition, AUTO_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [visibleCount, total]);

  const visibleRows = getVisibleItems(currentIndex);

  return (
    <Box
      sx={{
        ml: 2,
        mr: smallScreen ? 0 : 2,
        mb: 2,
        position: "relative",
      }}
    >
      <Box
        sx={{
          opacity: contentOpacity,
          transition: `opacity ${FADE_DURATION}ms ease`,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {visibleRows.map((row, rowIdx) => (
          <Box
            key={rowIdx}
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${slidesToShow}, 1fr)`,
              gap: "10px",
            }}
          >
            {row.map((item, colIdx) => (
              <Box
                key={`${rowIdx}-${colIdx}`}
                onClick={() => {
                  navigate(`/search?q=${item.query}`);
                }}
                sx={{
                  backgroundImage: `url(${item.bg?.default ?? item.bg})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  borderRadius: "0px",
                  height: isMobile ? 90 : 120,
                  cursor: "pointer",
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
                  },
                }}
              />
            ))}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          opacity: skeletonOpacity,
          transition: `opacity ${FADE_DURATION}ms ease`,
          pointerEvents: "none",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <Box
            key={rowIdx}
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${slidesToShow}, 1fr)`,
              gap: "10px",
            }}
          >
            {Array.from({ length: slidesToShow }).map((_, colIdx) => (
              <Box
                key={colIdx}
                sx={{
                  height: isMobile ? 90 : 120,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                  px: 3,
                  backgroundColor: "#ebebeb",
                  borderRadius: "0px",
                  overflow: "hidden",
                }}
              >
                <Skeleton
                  variant="rounded"
                  animation="wave"
                  sx={{
                    flexShrink: 0,
                    width: isMobile ? 50 : 64,
                    height: isMobile ? 50 : 64,
                    borderRadius: "8px",
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <Skeleton
                    variant="text"
                    animation="wave"
                    sx={{ width: "80%", height: 16, borderRadius: "4px" }}
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    sx={{ width: "55%", height: 16, borderRadius: "4px" }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default QueryCards;
